// Shared typed read model for the purchase/sales invoice workflow editor.
//
// Single home for the draft and approved shapes edited by both directions. Purchase and sales share this model; only the commercial
// pair differs (purchaseQuantity/purchaseUnit vs saleQuantity/saleUnit).
// Server-persisted documents (APPROVED/CANCELLED/UNKNOWN) render read-only.
//
// Unknown stays unknown: every money/rate/quantity field is nullable and the
// normalizers never fabricate zero, FX, totals or a physical unit. No network
// calls here.
import type { QuantityUnit } from '../utils/commercialQuantity';

export interface WorkflowInvoiceLineDimensions {
  width: number | null;
  height: number | null;
  length: number | null;
  thickness: number | null;
  productCategoryDimensionDisplayProfile: string | null;
}

export type WorkflowInvoiceDirection = 'PURCHASE' | 'SALES';

/** STANDARD generates stock movements on approval; others never do. */
export type WorkflowInvoiceKind = 'STANDARD' | 'CORRECTION' | 'ADVANCE' | 'SETTLEMENT' | 'PROFORMA' | 'UNKNOWN';

/**
 * Sales advance („Zaliczkowa”) and settlement („Rozliczeniowa”) invoices are
 * amount-only documents: no lines, the net is what they recognize
 * (backend salesAdvanceInvoiceService). SETTLEMENT exists only in sales.
 */
export const AMOUNT_INVOICE_KINDS: readonly WorkflowInvoiceKind[] = ['ADVANCE', 'SETTLEMENT'];
export function isAmountInvoice(invoice: { direction: WorkflowInvoiceDirection; kind: WorkflowInvoiceKind }): boolean {
  return invoice.direction === 'SALES' && AMOUNT_INVOICE_KINDS.includes(invoice.kind);
}

/**
 * Kind of a new sales draft opened from the ZK card (`?kind=` of
 * /app/sales-invoices/new): „Dodaj fakturę zaliczkową” → ADVANCE, „Wgraj
 * fakturę rozliczeniową” → SETTLEMENT, „Dodaj proformę” → PROFORMA. Anything
 * else keeps the ordinary invoice.
 */
const SALES_ENTRY_KINDS: readonly WorkflowInvoiceKind[] = ['ADVANCE', 'SETTLEMENT', 'PROFORMA'];
export function initialSalesInvoiceKind(param: string | null): WorkflowInvoiceKind {
  return SALES_ENTRY_KINDS.find(kind => kind === param) ?? 'STANDARD';
}

/**
 * A sales proforma is a ZK document that is only saved, never approved: no
 * sales, VAT, result or KSeF (decision 2026-10-27-platnosc-zk-proforma-faktoring).
 * Its screen offers no approval and asks for nothing an approval would.
 */
export function isSalesProforma(invoice: { direction: WorkflowInvoiceDirection; kind: WorkflowInvoiceKind }): boolean {
  return invoice.direction === 'SALES' && invoice.kind === 'PROFORMA';
}

/**
 * „Netto całego dokumentu” (decision 2026-10-27) belongs only to an ordinary
 * invoice that can be a part of one document on several orders; mirrors
 * backend `invoiceImportValidation.takesDocumentNetTotal`.
 */
export function takesDocumentNetTotal(invoice: { kind: WorkflowInvoiceKind }): boolean {
  return invoice.kind === 'STANDARD' || invoice.kind === 'UNKNOWN';
}

/**
 * DRAFT/EXTRACTED/READY are editable working states. APPROVED and CANCELLED
 * are server-persisted snapshots; UNKNOWN is a fail-closed read-only bucket
 * for any server status this frontend does not recognize — it must never
 * degrade into an editable DRAFT.
 */
export type WorkflowInvoiceStatus =
  | 'DRAFT'
  | 'EXTRACTED'
  | 'READY'
  | 'APPROVED'
  | 'CANCELLED'
  | 'UNKNOWN';

export type WorkflowExtractionStatus = 'IDLE' | 'PENDING' | 'SUCCESS' | 'STALE' | 'FAILED';

/**
 * Server-owned original OCR review context from
 * `sourceSnapshot.extractionReview`. `documentTotal` is the printed
 * unspecified TOTAL (never automatically net or gross) in the immutable
 * original `currency` captured at extraction time; `notes` are bounded
 * Polish uncertainty hints. Display-only: never an editable financial total
 * and never copied into submitted invoice monetary fields.
 */
export interface WorkflowInvoiceExtractionReview {
  documentTotal: number | null;
  /** Original OCR currency; null while unknown (never the live form currency). */
  currency: string | null;
  notes: string[];
}

/**
 * Sales line kind (migration 218): an ordinary product line, or the
 * non-product „Transport (refaktura)” — transport re-invoiced 1:1 to the
 * customer of a warehouse-sale ZK (decision 2026-11-03-refaktura-transportu).
 * It has no order line, no product and no physical quantity (1 szt.).
 */
export type WorkflowInvoiceLineKind = 'PRODUCT' | 'TRANSPORT_REINVOICE';

export const TRANSPORT_REINVOICE_LINE_NAME = 'Transport (refaktura)';

export function isTransportReinvoiceLine(line: Pick<WorkflowInvoiceLine, 'lineKind'>): boolean {
  return line.lineKind === 'TRANSPORT_REINVOICE';
}

export interface WorkflowInvoiceLine {
  key: string;
  /** Sales only; absent = an ordinary product line. */
  lineKind?: WorkflowInvoiceLineKind;
  /** Server identity; absent for not-yet-persisted editor lines. */
  id?: string | null;
  lineNumber?: number | null;
  productVariantId?: string | null;
  productName?: string | null;
  /** Explicit match to a source order line; required before approval. */
  sourceOrderLineId?: string | null;
  matchConfirmed: boolean;
  /** Entered physical amount; null while unknown (never a zero default). */
  unit?: QuantityUnit | null;
  quantity?: number | null;
  /** Canonical derivable set for the entered amount (pcs/m2/m3). */
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  conversionUnit?: QuantityUnit | null;
  conversionQuantity?: number | null;
  /** Canonical catalog geometry feeding the quantity conversion. */
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  /**
   * Display only: dimensions of a persisted line (source ZD/ZK line, catalog
   * fallback, category profile) shown under the name. Never feeds conversion.
   */
  productDimensions?: WorkflowInvoiceLineDimensions | null;
  /** Independent commercial price basis (purchase or sales side). */
  commercialQuantity?: number | null;
  commercialUnit?: QuantityUnit | null;
  unitPrice?: number | null;
  taxRate?: number | null;
  netAmount?: number | null;
  vatAmount?: number | null;
  grossAmount?: number | null;
  currency?: string | null;
  quantityRaw?: number | null;
  quantityUnitRaw?: string | null;
  discountPercent?: number | null;
  discountAmount?: number | null;
  /**
   * Piece count before an operator-confirmed rounding to whole pieces (the
   * printed amount gave e.g. 250,98 szt.); null = no rounding. Migration 214.
   */
  piecesRoundedFrom?: number | null;
}

/**
 * One selectable source order line supplied by the parent domain query.
 * Options render business labels (order number + product + remaining
 * quantity); IDs travel as values only, never as typed input.
 */
export interface WorkflowInvoiceSourceLineCandidate {
  sourceOrderLineId: string;
  sourceOrderId?: string;
  orderNumber: string;
  productName: string;
  productVariantId?: string | null;
  variantLabel?: string | null;
  remainingQuantity?: number | null;
  remainingUnit?: QuantityUnit | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  /**
   * Persisted physical quantity set of the source line (szt./m²/m³). An invoice
   * line matched to this source may only carry units present here; the server
   * scales the source snapshot and never backfills a persisted NULL from
   * catalog geometry (`normalizeLineQuantitySet`). Absent = unknown (legacy).
   */
  sourceQuantitySet?: { pcs: number | null; m2: number | null; m3: number | null } | null;
  /**
   * WP11: uninvoiced remainder of the source line as a physical set (the
   * source set scaled by remaining / ordered). Prefills a newly linked invoice
   * line that has no quantity yet. Absent = coverage unknown (no prefill).
   */
  remainingQuantitySet?: { pcs: number | null; m2: number | null; m3: number | null } | null;
  /** Commercial (price) unit of the source line (ZD purchase / ZK sale unit). */
  sourcePricingUnit?: QuantityUnit | null;
  /** Sales: how the ZK is fulfilled (Order.fulfillmentSource); gates the transport re-invoice. */
  fulfillmentSource?: 'WAREHOUSE' | 'FACTORY_DIRECT' | null;
}

/** WP15 (docs/decisions/2026-09-25-wp15-reklamacje.md): one complained ZK/ZD line. */
export interface WorkflowInvoiceComplaintLine {
  /** OrderProduct (sales) or SupplierOrderItem (purchase) id. */
  sourceLineId: string;
  quantity: number;
}

export interface WorkflowInvoice {
  /** Server-derived new purchase process; legacy and sales retain their prices. */
  netPurchasePrices?: boolean;
  /** Server identity; absent for not-yet-persisted editor drafts. */
  id?: string | null;
  /** Optimistic-lock version for draft updates; null until first persist. */
  version?: number | null;
  direction: WorkflowInvoiceDirection;
  kind: WorkflowInvoiceKind;
  status: WorkflowInvoiceStatus;
  extractionStatus: WorkflowExtractionStatus;
  extractionError?: string | null;
  extractionModel?: string | null;
  /** Original OCR review context; absent for rows without an extraction. */
  extractionReview?: WorkflowInvoiceExtractionReview | null;
  /** Original number printed on the received document; editable, never generated. */
  originalDocumentNumber?: string | null;
  sellerName?: string | null;
  sellerTaxId?: string | null;
  buyerName?: string | null;
  buyerTaxId?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  currency?: string | null;
  /** Unknown stays null; the editor never defaults FX. */
  exchangeRateToPLN?: number | null;
  exchangeRateDate?: string | null;
  exchangeRateSnapshot?: { rate: number; source: string; requestedDate: string; effectiveDate: string; tableNumber: string; currency?: string } | null;
  documentNotes?: string | null;
  totalNet?: number | null;
  totalVat?: number | null;
  totalGross?: number | null;
  settlementPeriod?: string | null;
  supplierOrderId?: string | null;
  sourceTransportId?: string | null;
  recognizedSupplierOrderId?: string | null;
  recognizedOrderId?: string | null;
  recognizedNetPLN?: number | null;
  supplierId?: string | null;
  /** Customer order link for sales drafts (backend `orderId`). */
  orderId?: string | null;
  correctsInvoiceId?: string | null;
  /** WP15: a correction with the reason „Reklamacja” (present only on a complaint). */
  correctionReason?: 'COMPLAINT' | null;
  /** WP15: complained quantity per ZK/ZD line, in that line's unit. */
  complaintLines?: WorkflowInvoiceComplaintLine[];
  coverageState?: 'PARTIAL' | 'FULL' | null;
  lines: WorkflowInvoiceLine[];
  linesLoaded: boolean;
  manuallyCorrected: boolean;
  approvedAt?: string | null;
  approvedBy?: string | null;
  sourceFileName?: string | null;
  approvalResult?: { pzDocuments: Array<{ id: string; pzNumber: string; sourceSupplierOrderId: string }>; orders: Array<{ id: string; commerciallyFrozen: boolean; coverageState: 'PARTIAL' | 'FULL' | null }>; replayed: boolean } | null;
  sourceFiles?: Array<{ name: string; sha256: string }>;
  extractionSeq?: number | null;
  lastAppliedExtractionSeq?: number | null;
  sellerAddress?: string | null;
  buyerAddress?: string | null;
  orderReference?: string | null;
  paymentTerms?: string | null;
  /**
   * Net of the whole document when it is split into parts on several orders
   * (decision 2026-10-27); stored in `sourceSnapshot.documentNetTotal`.
   */
  documentNetTotal?: number | null;
}

/** Editable draft the editor owns; persisted snapshots are read-only. */
export type WorkflowInvoiceDraft = WorkflowInvoice;

export function isStockGeneratingKind(kind: WorkflowInvoiceKind): boolean {
  return kind === 'STANDARD';
}

export function isApprovedSnapshot(invoice: Pick<WorkflowInvoice, 'status'>): boolean {
  return invoice.status === 'APPROVED';
}

/** Only working states are editable; everything persisted is read-only. */
export function isEditableWorkflowStatus(status: WorkflowInvoiceStatus): boolean {
  return status === 'DRAFT' || status === 'EXTRACTED' || status === 'READY';
}
